import { addHours, isToday } from 'date-fns';
import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company, DEFAULT_COMPANIES } from '~/company/Company';
import DeliveryDateLabel from '~/components/DeliveryDateLabel/DeliveryDateLabel';
import { formatVolume, pluralizePcLabel } from '~/components/ProductCard/utils';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useDateLang } from '~/hooks/useDateLang';
import { useProductLimit } from '~/hooks/useProductLimit';
import { cartStore, mainStore, orderStore } from '~/stores';
import type { CartItem, CartItemOffer } from '~/stores/CartStore';
import { isProductBundle } from '~/types/Product';
import { formatPriceWithCurrency, toNumber } from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
} from '~/utils/multibuy';

import Checkbox from '../CheckboxCustom/Checkbox';
import CloudflareResponseImage from '../CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '../CloudflareResponsiveImage/constants';
import Counter from '../Counter';
import DiscountBadge from '../DiscountBadge/DiscountBadge';
import ExpressDeliveryBadge from '../ExpressDeliveryBadge/ExpressDeliveryBadge';
import Price from '../Price';
import Tile from '../Tile';

import styles from './CartCard.module.scss';
import { RestrictionBlock } from './components/RestrictionBlock/RestrictionBlock';
import CartCardVariant1 from './Variant1/CartCard';

interface CartCardProps {
  item: CartItem;
  size?: 's' | 'm' | 'l';
  location?: 'modalCart' | 'productPreview';
  className?: string;
  showCheckbox?: boolean;
}

const CartCard = ({
  item,
  size = 's',
  className,
  location,
  showCheckbox,
}: CartCardProps) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();
  const { limit, currentWhSellable, parentWhSellable, laterCount } =
    useProductLimit({
      offer: item,
    });
  const { locale } = useDateLang();

  const isBundle = isProductBundle(item);

  if (DEFAULT_COMPANIES.includes(company.name)) {
    return (
      <CartCardVariant1
        item={item}
        location={location}
        size={size}
        className={className}
        showCheckbox={showCheckbox}
      />
    );
  }

  const handleChangeCount = (count: number, action: 'add' | 'remove') => {
    if (item) {
      cartStore.setCartItemCountByProduct(item, count, action, 'product_main');
    }
  };

  const applicableLevel =
    !isBundle && item.multibuyPriceLevels?.length
      ? getApplicableMultibuyLevel(item.multibuyPriceLevels, item.count)
      : null;
  const isMultiBuy = !isBundle && Boolean(item.multibuyPriceLevels?.length);
  const isMultibuyActivated = isMultiBuy && applicableLevel !== null;
  const isExpress =
    item &&
    currentWhSellable &&
    currentWhSellable >= item.count &&
    orderStore.isExpressAvailableNow;
  const isBackorderAvailable =
    item.isBackorderAvailable && item.backorderLeadTime && parentWhSellable > 0;

  const volume = isBundle
    ? `• ${item.bundleQuantity ?? company.config.defaultVolumeDisplay}`
    : formatVolume(item.properties, {
        prefix: '•',
        defaultVolume: company.config.defaultVolumeDisplay,
      });

  const leadTime = item.backorderLeadTime ? item.backorderLeadTime : 0;

  const price =
    !isMultiBuy && item.discountPrice ? item.discountPrice : item.price;

  const renderPriceComponent = () => {
    const classNames = {
      classNameCurrentPrice: styles['cart-card__info-footer-price'],
    };

    if (isMultiBuy && applicableLevel) {
      return (
        <Price
          price={mainStore.convertPenceToPounds(
            applicableLevel.unitPrice * item.count,
          )}
          discount={mainStore.convertPenceToPounds(price * item.count)}
          classes={classNames}
        />
      );
    }

    if (item.discountPrice) {
      return (
        <Price
          price={mainStore.convertPenceToPounds(price * item.count)}
          discount={mainStore.convertPenceToPounds(item.price * item.count)}
          classes={classNames}
        />
      );
    }

    return (
      <Price
        price={mainStore.convertPenceToPounds(price * item.count)}
        classes={classNames}
      />
    );
  };

  let selectablePropsList = null;
  if (!isBundle && Object.keys(item.selectableProperties ?? {}).length > 0) {
    selectablePropsList = (
      <ul className={styles.propsList}>
        {Object.values((item as CartItemOffer).selectableProperties).map(
          (item, id) => (
            <li key={id} className={styles.propsListItem}>
              <span className={styles.propsListItemName}>{item.name}:</span>{' '}
              <span className={styles.propsListItemValue}>{item.value}</span>
            </li>
          ),
        )}
      </ul>
    );
  }

  const getDeliveryBadge = () => {
    if (isExpress && company.showExpressBadgeOnProductCards) {
      return (
        <ExpressDeliveryBadge
          className={styles.deliveryDateLabel}
          size={'xxs'}
        />
      );
    }

    if (isBackorderAvailable) {
      return (
        <DeliveryDateLabel
          size={'xxs'}
          className={styles.deliveryDateLabel}
          deliveryDate={
            isToday(addHours(new Date(), leadTime))
              ? null
              : addHours(new Date(), leadTime)
          }
        />
      );
    }

    return null;
  };

  return (
    <div id="cart-card" className={styles.container}>
      <div className={classes([styles['cart-card'], `_${size}`, className])}>
        <Link
          data-link="cart-card-image"
          onClick={handleClickProduct(item)}
          to={`/p/${item.slug}` + (isBundle ? '' : `?sku=${item.sku}`)}
          state={{ ...(isProductBundle(item) ? {} : { offerSku: item.sku }) }}
        >
          <Tile className={styles['cart-card__tile']}>
            {/* TODO: express badges */}
            <div className={styles.badgeList}>
              {getDeliveryBadge()}
              {item.price && toNumber(price) < toNumber(item.price) && (
                <DiscountBadge
                  currentPrice={toNumber(price)}
                  oldPrice={toNumber(item.price)}
                  size={'xxs'}
                  theme="primary"
                />
              )}
            </div>
            <CloudflareResponseImage
              data-image="cart-card"
              src={item.previewImageThumb || ''}
              srcParams={{ width: 100, height: 140 }}
              srcSetParams={defaultSrcSetParams}
              alt=""
              className={styles['cart-card__tile__image']}
              loading="lazy"
            />
          </Tile>
        </Link>
        <div className={styles['cart-card__info']}>
          <div className={styles['cart-card__info-header']}>
            <div className={styles['cart-card__info-header-content']}>
              <Link
                data-link="cart-card-header"
                onClick={handleClickProduct(item)}
                to={`/p/${item.slug}` + (isBundle ? '' : `?sku=${item.sku}`)}
                state={{
                  ...(isProductBundle(item) ? {} : { offerSku: item.sku }),
                }}
              >
                <p className={styles.itemName}>{item.name}</p>
              </Link>
              <p
                className={classes([
                  styles['cart-card__info-header-content-unit'],
                  !isMultiBuy && item.discountPrice > 0 && styles.hasDiscount,
                  isMultibuyActivated && styles.hasDiscount,
                ])}
              >
                {formatPriceWithCurrency(
                  mainStore.convertPenceToPounds(
                    isMultiBuy && applicableLevel
                      ? applicableLevel.unitPrice
                      : price,
                  ),
                )}
                {((!isMultiBuy && item.discountPrice > 0) ||
                  isMultibuyActivated) && (
                  <span className={styles.oldPrice}>
                    {formatPriceWithCurrency(
                      mainStore.convertPenceToPounds(item.price),
                    )}
                  </span>
                )}
                <span
                  className={classes([
                    styles.volume,
                    !isMultiBuy &&
                      item.discountPrice > 0 &&
                      styles.discountedValue,
                    isMultibuyActivated && styles.discountedValue,
                  ])}
                >
                  &nbsp;
                  {volume}
                </span>
              </p>
              {isBundle && (
                <div className={styles.specialOffer}>
                  {t('specialOffer')} -{' '}
                  <span className={styles.type}>{t('bundle')}</span>
                </div>
              )}
            </div>
            {showCheckbox && (
              <Checkbox
                attributeName="cart-card-select"
                onChange={() => cartStore.handleSelectedCartItem(item.sku)}
                checked={item.selected}
              />
            )}
          </div>
          {selectablePropsList}
          {isMultiBuy &&
            (() => {
              const lowestLevel = getLowestMultibuyLevel(
                item.multibuyPriceLevels,
              );
              if (!lowestLevel) {
                return null;
              }
              return (
                <div className={styles.multiBuySpecialOfferWr}>
                  <span className={styles.multiBuySpecialOffer}>
                    {`${t('phrases:specialOffer')}: `}
                    <span className={styles.multiBuySpecialOffer__bold}>
                      {t('phrases:multiBuySpecialOffer', {
                        cost: formatPriceWithCurrency(
                          mainStore.convertPenceToPounds(lowestLevel.unitPrice),
                        ),
                        length: pluralizePcLabel(lowestLevel.quantity),
                      })}
                    </span>
                  </span>
                </div>
              );
            })()}
          <div className={styles['cart-card__info-footer']}>
            {renderPriceComponent()}
            <Counter
              count={item.count}
              onClick={handleChangeCount}
              size={size}
              limit={limit}
            />
          </div>
        </div>
      </div>
      {currentWhSellable > 0 && item.count > currentWhSellable ? (
        <RestrictionBlock
          date={format(addHours(new Date(), leadTime), 'd MMMM', {
            timeZone: company.config.timeZone,
            locale,
          })}
          currentCount={currentWhSellable}
          laterCount={laterCount}
        />
      ) : null}
    </div>
  );
};

export default observer(CartCard);
